import React, { useState, useEffect, useRef } from 'react';
import { Flex, Image, Text, Icon, Skeleton } from '@chakra-ui/react';
import { FaStoreAlt } from 'react-icons/fa';
import { MarketplaceInfo } from 'src/api/v1-api/';
import { srcFromCDN } from 'src/utils/files';
import NextLink from 'next/link';

interface LinkInBioHeaderProps {
  marketplace: MarketplaceInfo | null;
}

const LinkInBioHeader = ({ marketplace }: LinkInBioHeaderProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  // Reset loading state when marketplace or logo changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [marketplace?.id, marketplace?.logo_image]);

  // Check if image is already loaded (e.g., from cache)
  useEffect(() => {
    if (marketplace && imageRef.current) {
      const hasLogo = !!marketplace?.logo_image;
      if (hasLogo && !imageLoaded && !imageError) {
        const img = imageRef.current;

        // If image is already complete (loaded from cache), set loaded state
        if (img.complete && img.naturalWidth > 0) {
          setImageLoaded(true);
        }
      }
    }
  }, [marketplace?.logo_image, imageLoaded, imageError]);

  if (!marketplace) return null;

  const hasLogo = !!marketplace?.logo_image;
  const logoSrc = hasLogo ? srcFromCDN(marketplace.logo_image, 94) : '';
  const showSkeleton = hasLogo && !imageLoaded && !imageError;
  const showFallback = !hasLogo || imageError;

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <NextLink href="/">
      <Flex alignSelf="center" flexDirection="column" alignItems="center" mb="5" cursor="pointer">
        <Flex
          position="relative"
          boxSize="94px"
          borderRadius="full"
          alignItems="center"
          justifyContent="center"
        >
          {showSkeleton && (
            <Skeleton position="absolute" borderRadius="full" height="94px" width="94px" />
          )}
          {showFallback && <Icon as={FaStoreAlt} color="black" boxSize="6" />}
          {hasLogo && (
            <Image
              ref={imageRef}
              src={logoSrc}
              alt="Logo"
              boxSize="94px"
              objectFit="cover"
              borderRadius="full"
              transition="transform 0.2s"
              _hover={{ transform: 'scale(1.05)' }}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.2s ease-in-out',
              }}
            />
          )}
        </Flex>
        <Text
          mt={4}
          fontWeight="bold"
          fontSize="xl"
          _hover={{ color: 'blue.500' }}
          transition="color 0.2s"
        >
          {marketplace?.name || ''}
        </Text>
      </Flex>
    </NextLink>
  );
};

export default LinkInBioHeader;
